import { useCallback, useMemo, useRef } from 'react'

import type { QueryKey } from 'react-query'
import { useQueryClient } from 'react-query'

import { useQueryGetCustomerGatewayBalances } from '@cash-web/shared-data-access-api/react-query'
// eslint-disable-next-line @nx/workspace-no-new-franklin-get-profile -- migration support: only the root query key is read, to keep the legacy GetProfile cache in sync while it remains a balance source (CWP-2327)
import { useQueryGetProfile } from '@cash-web/shared-data-access-api/react-query/useQueryGetProfile'
import {
  FlowData,
  FlowEvents,
  FlowInitialProps,
  FlowQueriesToInvalidate,
  FlowQueriesToRemove,
} from '@cash-web/shared-flow-data-access-context'

const matchesRootQueryKey = (queryKey: QueryKey, rootQueryKey: string) =>
  queryKey === rootQueryKey || (Array.isArray(queryKey) && queryKey[0] === rootQueryKey)

// CWP-2327 getProfile balance migration: flows that invalidate CCG balances must also invalidate
// GetProfile while both remain balance sources, or the getProfile-derived balance goes stale.
// Remove once the migration completes.
const withPairedBalanceInvalidation = (queryKeys: QueryKey[]): QueryKey[] => {
  const invalidatesCcgBalances = queryKeys.some(queryKey =>
    matchesRootQueryKey(queryKey, useQueryGetCustomerGatewayBalances.rootQueryKey)
  )
  const invalidatesProfile = queryKeys.some(queryKey => matchesRootQueryKey(queryKey, useQueryGetProfile.rootQueryKey))
  return invalidatesCcgBalances && !invalidatesProfile ? [...queryKeys, useQueryGetProfile.rootQueryKey] : queryKeys
}

export type FlowListener = FlowEvents &
  FlowQueriesToInvalidate &
  FlowQueriesToRemove & {
    shouldRevalidateRoute?: boolean
  }

const getListenerData = (data: FlowData | undefined) => {
  if (
    data == null ||
    (data.onCompleteFlow == null &&
      data.onCancelFlow == null &&
      data.onFlowResponse == null &&
      data.queriesToInvalidate == null &&
      data.shouldRevalidateRoute == null)
  ) {
    return undefined
  }
  return {
    onCompleteFlow: data.onCompleteFlow,
    onCancelFlow: data.onCancelFlow,
    onFlowResponse: data.onFlowResponse,
    queriesToInvalidate: data.queriesToInvalidate,
    queriesToRemove: data.queriesToRemove,
    shouldRevalidateRoute: data.shouldRevalidateRoute,
  }
}

export type UseFlowListenersProps = {
  initialFlow?: FlowInitialProps
  onRevalidateRoute?: () => void
}

export const useFlowListeners = ({ initialFlow, onRevalidateRoute }: UseFlowListenersProps) => {
  const initialListener = initialFlow ? getListenerData(initialFlow) : undefined

  const queryClient = useQueryClient()
  const childFlowListener = useRef<FlowListener[]>(initialListener ? [initialListener] : [])
  const suspended = useRef<Record<string, FlowListener[]>>({})

  const clear = useCallback(() => {
    childFlowListener.current = initialListener ? [initialListener] : []
  }, [initialListener])

  const reset = useCallback(() => {
    clear()
    suspended.current = {}
  }, [clear])

  const invalidateQueries = useCallback(async () => {
    for (const listener of childFlowListener.current) {
      if (listener.queriesToInvalidate === 'ALL') {
        queryClient.invalidateQueries()
      } else {
        await Promise.all(
          withPairedBalanceInvalidation(listener.queriesToInvalidate).map(queryKey =>
            queryClient.invalidateQueries(queryKey)
          )
        )
      }
    }
  }, [queryClient])

  const removeQueries = useCallback(async () => {
    for (const listener of childFlowListener.current) {
      if (listener.queriesToRemove) {
        await Promise.all(listener.queriesToRemove.map(queryKey => queryClient.removeQueries(queryKey)))
      }
    }
  }, [queryClient])

  const onCompleteFlow = useCallback(async () => {
    void invalidateQueries()
    void removeQueries()
    let revalidationComplete = false
    for (const listener of childFlowListener.current) {
      listener.onCompleteFlow?.()
      if (!revalidationComplete && listener.shouldRevalidateRoute) {
        revalidationComplete = true
        void onRevalidateRoute?.()
      }
    }
    clear()
  }, [invalidateQueries, removeQueries, clear, onRevalidateRoute])

  const onCancelFlow = useCallback(async () => {
    void invalidateQueries()
    void removeQueries()
    let revalidationComplete = false
    for (const listener of childFlowListener.current) {
      listener.onCancelFlow?.()
      if (!revalidationComplete && listener.shouldRevalidateRoute) {
        revalidationComplete = true
        void onRevalidateRoute?.()
      }
    }
    clear()
  }, [invalidateQueries, removeQueries, clear, onRevalidateRoute])

  const onFlowResponse = useCallback((status: string) => {
    for (const listener of childFlowListener.current) {
      listener.onFlowResponse?.(status)
    }
  }, [])

  const add = useCallback((data: FlowData) => {
    const listener = getListenerData(data)

    if (listener == null) {
      return
    }

    childFlowListener.current = [...childFlowListener.current, listener]
  }, [])

  const suspend = useCallback((id: string) => {
    suspended.current[id] = childFlowListener.current
    childFlowListener.current = []
  }, [])

  const restore = useCallback((id: string) => {
    const { [id]: next, ...rest } = suspended.current
    if (next) {
      childFlowListener.current = next
      suspended.current = rest
    }
  }, [])

  return useMemo(
    () => ({ add, suspend, restore, reset, onCompleteFlow, onCancelFlow, onFlowResponse, invalidateQueries }),
    [add, suspend, restore, reset, invalidateQueries, onCancelFlow, onCompleteFlow, onFlowResponse]
  )
}
